/**
 * Per-user subscription overrides service — ADR 116 Tier 3.
 *
 * Backed by the v2 endpoints:
 *   GET    /api/v2/users/{userId}/subscriptions/
 *   PUT    /api/v2/users/{userId}/subscriptions/{alertTypeId}/
 *   DELETE /api/v2/users/{userId}/subscriptions/{alertTypeId}/
 *
 * Semantics worth knowing at the call site:
 *   - Rows carry BOTH the effective values (what delivery uses) and the
 *     `resolved_*` rubric values (what "Role default" currently means).
 *     They differ only when `is_override` is true.
 *   - PUT with an absent key preserves that axis; an explicit `null`
 *     unsubscribes it. DELETE reverts the WHOLE row (both axes) to the
 *     rubric — per-axis revert is not representable (row-level source).
 *   - Server guards: emergency can't be unsubscribed (400
 *     `emergency_unsubscribe`); `follow`/`group` locked types reject all
 *     overrides; self explicit-null writes and all on-behalf access
 *     need `accounts.can_manage_subscription` (403).
 *
 * Hand-rolled APIViews → `client.ky` passthrough, same as
 * `rubricCells.service.ts`.
 */
import { client } from '../lib/http-client';

export type SubscriptionSource =
  | 'rubric_default'
  | 'rubric_type_override'
  | 'user_explicit';

export type SubscriptionLock = 'follow' | 'group' | null;

export interface UserSubscriptionAlertType {
  id: number;
  name: string;
  icon: string | null;
  urgency: 'emergency' | 'attention' | 'follow';
  activation_mode: string;
  school: number;
  is_system: boolean;
}

export interface UserSubscriptionRow {
  alert_type: UserSubscriptionAlertType;
  /** Effective minimum levels (null = unsubscribed from that phase). */
  alert_min_level: number | null;
  drill_min_level: number | null;
  source: SubscriptionSource;
  is_override: boolean;
  /** What "Role default" resolves to right now. */
  resolved_alert_min_level: number | null;
  resolved_drill_min_level: number | null;
  locked: SubscriptionLock;
  /** False for emergency — the alert axis can never be unsubscribed. */
  allow_unsubscribe: boolean;
}

export interface UserSubscriptionOverridePayload {
  /** Omit a key to preserve that axis; explicit null unsubscribes it. */
  alert_min_level?: number | null;
  drill_min_level?: number | null;
}

function basePath(userId: number): string {
  return `api/v2/users/${userId}/subscriptions/`;
}

export async function getUserSubscriptions(
  userId: number,
  /** Optional site scope. Narrows to one of the user's sites;
   *  omit for the flat all-sites list. */
  school?: number | null,
): Promise<{ results: UserSubscriptionRow[] }> {
  const response = await client.ky.get(
    basePath(userId),
    school != null ? { searchParams: { school } } : undefined,
  );
  return (await response.json()) as { results: UserSubscriptionRow[] };
}

export async function setUserSubscriptionOverride(
  userId: number,
  alertTypeId: number,
  body: UserSubscriptionOverridePayload,
): Promise<UserSubscriptionRow> {
  const response = await client.ky.put(`${basePath(userId)}${alertTypeId}/`, {
    json: body,
  });
  return (await response.json()) as UserSubscriptionRow;
}

export async function clearUserSubscriptionOverride(
  userId: number,
  alertTypeId: number,
): Promise<UserSubscriptionRow> {
  const response = await client.ky.delete(`${basePath(userId)}${alertTypeId}/`);
  return (await response.json()) as UserSubscriptionRow;
}
